<template>
    <b-container fluid>
        <h1>{{ pageTitle }}</h1>
        <!-- No content for overview -->
        <div v-show="noData" class="col-md-12">
            <span>{{ $t('styling.patterns.patternsNonePresent') }}</span>
        </div>
        <br />
        <div class="col-xs-12 col-md-10 col-lg-8">
            <b-form
                name="polygonPatternsForm"
                v-on:submit.prevent="$_onSubmit"
                class="pb-5"
            >
                <!-- Pattern type radio buttons -->
                <b-form-group v-if="!isSuperUserForm">
                    <b-form-radio-group
                        id="type-radio-group"
                        name="type-radio-sub-component"
                        v-model="sourceType"
                        @change="$_onTypeChanged"
                    >
                        <b-form-radio :value="SOURCE_TYPES.organization">
                            {{
                                $t('styling.patterns.titleUserPolygonPatterns')
                            }}
                        </b-form-radio>
                        <b-form-radio :value="SOURCE_TYPES.default">
                            {{
                                $t(
                                    'styling.patterns.titleDefaultPolygonPatterns'
                                )
                            }}
                        </b-form-radio>
                    </b-form-radio-group>
                </b-form-group>
                <b-row v-if="canAddDelete">
                    <b-col cols="6" md="3">
                        <!-- Upload -->
                        <b-form-group
                            id="fieldset-1"
                            :label="$t('styling.patterns.uploadLabel')"
                            :label-for="name"
                        >
                            <div :id="`input_${name}`" class="upload-group">
                                <div
                                    class="upload-control"
                                    v-on:click="$_selectFile"
                                >
                                    <em
                                        class="fas fa-plus-circle upload-icon"
                                    ></em>
                                    <span
                                        :id="`selected_${name}`"
                                        class="upload-name"
                                        >{{ fileName }}</span
                                    >
                                </div>
                                <!-- Invisible file selector -->
                                <input
                                    v-bind="$attrs"
                                    ref="fileSelector"
                                    v-show="false"
                                    type="file"
                                    accept=".svg"
                                    :id="name"
                                    :name="name"
                                    @change="$_onFileSelected"
                                    :data-fieldname="name"
                                />
                            </div>
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <!-- Upload button -->
                        <b-button
                            type="submit"
                            id="save_button"
                            variant="primary"
                            class="d-flex align-items-center"
                            :disabled="
                                isUploading || isDeleting || isBtnSaveDisabled
                            "
                        >
                            {{ $t('styling.patterns.btnUploadLabel') }}
                            <span
                                class="ml-1 fas"
                                :class="{
                                    'fa-check': !isUploading,
                                    'fa-sync-alt fa-spin': isUploading
                                }"
                            >
                            </span>
                        </b-button>
                    </b-col>
                </b-row>
                <b-table
                    v-if="!noData"
                    responsive
                    striped
                    hover
                    small
                    outlined
                    bordered
                    :items="polygonPatterns"
                    :sort-by.sync="sortBy"
                    :fields="filteredFields"
                    sort-icon-left
                >
                    <!-- Pattern -->
                    <template v-slot:cell(imageData)="row">
                        <svg-loader
                            class="img-fluid"
                            :source="row.item.fileUrl"
                            :settings="svgSettings"
                        >
                        </svg-loader>
                    </template>
                    <!-- Delete button -->
                    <template v-slot:cell(delete)="row">
                        <div class="d-flex justify-content-center">
                            <a
                                href="#"
                                class="delete"
                                @click="$_onDeletePattern(row.item)"
                                :title="$t('btnDeleteLabel')"
                            >
                                <em
                                    class="fas fa-times"
                                    aria-hidden="true"
                                ></em>
                            </a>
                        </div>
                    </template>
                </b-table>
                <!-- Cancel button -->
                <div class="sticky-bottom fixed-bottom">
                    <b-button
                        id="cancel_button"
                        @click="$_onCancel"
                        class="d-flex align-items-center btn-space-5"
                        :disabled="isUploading || isDeleting"
                    >
                        {{ $t('btnCancelLabel') }}
                        <span class="ml-1 fas fa-times"></span>
                    </b-button>
                </div>
            </b-form>
        </div>
    </b-container>
</template>

<script>
    import { nprogress } from '../../services/utilities';
    import { ICON_UNITS } from '../../constants/api';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import SvgLoader from '../../components/BaseComponents/SvgLoader.vue';

    const SOURCE_TYPES = {
        organization: 'organization',
        default: 'default'
    };

    export default {
        mixins: [modalMixin],

        props: { user: { type: String, default: 'unknown' } },

        components: { 'svg-loader': SvgLoader },

        data() {
            return {
                noData: true,
                isUploading: false,
                isDeleting: false,
                mode: 0,
                name: 'PolygonPatterns',
                isBtnSaveDisabled: true,
                fileName: this.$t('styling.patterns.noFileSelected'),
                allPolygonPatterns: [],
                polygonPatterns: [],
                // table fields
                fields: [
                    {
                        key: 'fileName',
                        label: this.$t('styling.patterns.fileName'),
                        sortable: true,
                        tdClass: 'align-middle'
                    },
                    {
                        key: 'imageData',
                        label: this.$t('styling.patterns.filePattern'),
                        sortable: false,
                        tdClass: 'align-middle'
                    },
                    {
                        key: 'delete',
                        label: '',
                        sortable: false,
                        tdClass: 'align-middle'
                    }
                ],
                sortBy: 'blobFileEntry.fileName',
                authorisations: null,
                defaultContainerSource: 'default',
                organizationContainerSource: 'organization',
                // super-user identifier (from the router route)
                superUser: '21f4a37b-bd40-4e2a-a0fe-ae98dbcf75d0',
                svgSettings: {
                    fillSvg: 'rgba(00,00,00,1)',
                    fillOpacitySvg: 1,
                    strokeSvg: 'rgba(00,00,255,1)',
                    strokeOpacitySvg: 1,
                    strokeWidthSvg: 1,
                    sizeSvg: 30,
                    sizeUnits: ICON_UNITS.pixels
                },
                SOURCE_TYPES: SOURCE_TYPES,
                sourceType: SOURCE_TYPES.organization
            };
        },

        async mounted() {
            this.authorisations = await this.$store.dispatch(
                'getUserAuthentication'
            );
            this.$_getAllStylePatterns();
        },

        computed: {
            isSuperUserForm() {
                return this.user === this.superUser;
            },
            pageTitle() {
                return this.isSuperUserForm
                    ? this.$t('styling.patterns.titleDefaultPolygonPatterns')
                    : this.$t('styling.patterns.titlePolygonPatterns');
            },
            source() {
                return this.isSuperUserForm
                    ? this.defaultContainerSource
                    : this.organizationContainerSource;
            },
            canAddDelete() {
                return (
                    this.isSuperUserForm ||
                    this.sourceType === SOURCE_TYPES.organization
                );
            },
            filteredFields() {
                // If the user cannot add/delete, filter out the delete column
                return this.canAddDelete
                    ? this.fields
                    : this.fields.filter((field) => field.key !== 'delete');
            }
        },

        methods: {
            async $_getAllStylePatterns() {
                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'PolygonSvgFiles',
                        parameters: {}
                    });

                    this.noData = result.data && result.data.length === 0;
                    this.allPolygonPatterns = Object.assign([], result.data);
                    this.polygonPatterns = this.allPolygonPatterns.filter(
                        (p) => p.source === this.source
                    );
                } catch (error) {
                    const data = {
                        message: this.$t(error.message),
                        variant: 'danger',
                        title: this.$t(error.title),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            async $_onDeletePattern(item) {
                try {
                    this.isDeleting = true;
                    nprogress.start();

                    await this.$store.dispatch('useApiData', {
                        name: 'DeletePolygonSvgFile',
                        data: {
                            fileName: item.fileName,
                            source: this.source
                        },
                        config: { timeout: 0 }
                    });
                } catch (error) {
                    let data = null;

                    if (error.data.response.status == 404) {
                        data = {
                            message: error.message,
                            variant: 'warning',
                            autoHideDelay: 5000
                        };
                    } else {
                        data = {
                            message: error.message,
                            variant: 'danger',
                            autoHideDelay: 5000
                        };
                    }

                    this.showToast(data);
                } finally {
                    await this.$_getAllStylePatterns();

                    nprogress.done();
                    this.isDeleting = false;
                    this.isBtnSaveDisabled = true;
                }
            },

            $_onTypeChanged(value) {
                if (value === SOURCE_TYPES.organization) {
                    this.polygonPatterns = this.allPolygonPatterns.filter(
                        (p) => p.source === this.organizationContainerSource
                    );
                } else {
                    this.polygonPatterns = this.allPolygonPatterns.filter(
                        (p) => p.source === this.defaultContainerSource
                    );
                }
            },

            /**
             * Show the import file selector
             */
            $_selectFile() {
                // call click on the hidden file selector
                this.$refs.fileSelector.click();
            },

            /**
             * Read the zip content
             * @param  {Array} files   [selected zip files]
             * @return {object}        [Promise object]
             */
            readFileContent(files) {
                return new Promise((resolve, reject) => {
                    // het geselecteerde svg bestand
                    const file = files[0];
                    if (file && file.size > 20971520) {
                        reject(new Error('Bestand is te groot'));
                        return;
                    }

                    try {
                        // read file
                        const reader = new FileReader();
                        reader.onload = (e) => {
                            const data = e.target.result.split(',')[1];
                            const type = e.target.result.split(';')[0];
                            // result
                            resolve({
                                data,
                                type
                            });
                        };
                        reader.readAsDataURL(file);
                    } catch (error) {
                        reject(error);
                    }
                });
            },

            async $_onFileSelected(event) {
                const files = event.currentTarget.files;
                if (files.length > 0) {
                    this.fileName = files[0].name;

                    // read file content
                    this.readFileContent(files).then(
                        (result) => {
                            this.uploadData = result.data;
                            this.isBtnSaveDisabled = false;
                        },
                        (error) => {
                            const data = {
                                message: this.$t(error.message),
                                variant: 'danger',
                                title: this.$t('styling.patterns.patternFileError'),
                                autoHideDelay: 10000
                            };
                            this.showToast(data);
                        }
                    );
                } else {
                    this.fileName = this.$t('styling.patterns.noFileSelected');
                    this.isBtnSaveDisabled = true;
                }
            },

            async $_onSubmit() {
                try {
                    this.isUploading = true;
                    nprogress.start();

                    await this.$store.dispatch('useApiData', {
                        name: 'UploadPolygonSvgFile',
                        data: {
                            fileName: this.fileName,
                            svgImageContent: this.uploadData,
                            source: this.source
                        },
                        config: { timeout: 0 }
                    });
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                } finally {
                    await this.$_getAllStylePatterns();

                    nprogress.done();
                    this.isUploading = false;
                    this.fileName = this.$t('styling.patterns.noFileSelected');
                    this.isBtnSaveDisabled = true;

                    // Reset the file value so that the same file can be selected again
                    this.$refs.fileSelector.value = '';
                }
            },

            $_onCancel() {
                this.$router.push({ path: '/' });
            }
        }
    };
</script>

<style scoped>
.upload-group {
    display: block;
    margin: 0px;
    padding: 0px;
    width: 100%;
    height: calc(1.5em + 0.75rem + 2px);
}

.upload-control {
    background: #fff;
    cursor: pointer;
    border-radius: 0.25rem;
}

.upload-icon {
    color: #007ac9;
    margin-left: 10px;
}

.upload-name {
    margin-left: 10px;
    margin-right: 15px;
}

.img-fluid {
    max-width: 30px !important;
    max-height: 30px !important;
}

.delete {
    color: red;
    padding-left: 0.2em;
    padding-right: 0.2em;
}
</style>
